import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';

// Copy of PERMISSION_LABELS (Frontend needs this too, ideally shared but usually duplicated in dual-repo)
// In a monorepo we'd import, but for now we define locally or fetch from API types.
const PERMISSION_LABELS: Record<number, string> = {
    1: "活動停止中",
    2: "コメント不可",
    4: "メディア制限",
    8: "音声機能無効",
    16: "Shadow Ban",
    32: "DM制限",
    64: "投票制限",
    128: "認証済み",
};

export const getActiveRestrictions = (flags: number) => {
    const labels = [];
    for (const [key, label] of Object.entries(PERMISSION_LABELS)) {
        const flag = parseInt(key);
        if (flag === 128) continue; // Skip Verified
        if ((flags & flag) === flag) labels.push(label);
    }
    return labels;
};

interface UserDetailProps {
    user: {
        id: string;
        display_name: string;
        icon_url: string;
        created_at: number;
        flags: number;
        status: string;
    };
    onPunish: () => void;
    onClose: () => void;
}

export const UserDetailCard = ({ user, onPunish, onClose }: UserDetailProps) => {
    const restrictions = getActiveRestrictions(user.flags);

    return (
        <Card className="w-full h-full bg-ink/80 backdrop-blur-xl border-t border-ink rounded-t-3xl shadow-2xl flex flex-col">
            <div className="w-12 h-1 bg-cream-2 rounded-full mx-auto mt-3 mb-6" onClick={onClose} />

            <CardHeader className="text-center pt-0 pb-6">
                <div className="w-24 h-24 rounded-full border-4 border-ink/15 mx-auto mb-4 overflow-hidden">
                    <img src={user.icon_url || '/placeholder.png'} alt={user.display_name} className="w-full h-full object-cover" />
                </div>
                <CardTitle className="text-2xl font-bold text-ink">{user.display_name}</CardTitle>
                <p className="text-xs text-muted-foreground font-mono mt-1">{user.id}</p>

                <div className="flex gap-2 justify-center mt-3 flex-wrap">
                    <Badge variant={user.status === 'active' ? 'outline' : 'destructive'}>{user.status.toUpperCase()}</Badge>
                    {(user.flags & 128) > 0 && <Badge variant="neon">VERIFIED</Badge>}
                </div>
            </CardHeader>

            <CardContent className="flex-1 space-y-6 overflow-y-auto">
                {/* Active Restrictions */}
                {restrictions.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <h4 className="text-red-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                            <ShieldAlert size={14} /> 現在のアクティブな処罰
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {restrictions.map(r => (
                                <Badge key={r} variant="destructive" className="text-xs">{r}</Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Stats (Mock for now, would be Sparkline) */}
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-cream rounded-lg p-3">
                        <div className="text-muted-foreground text-xs mb-1">作品数</div>
                        <div className="text-xl font-display font-bold">12</div>
                    </div>
                    <div className="bg-cream rounded-lg p-3">
                        <div className="text-muted-foreground text-xs mb-1">通報数</div>
                        <div className="text-xl font-display font-bold text-yellow-500">2</div>
                    </div>
                    <div className="bg-cream rounded-lg p-3">
                        <div className="text-muted-foreground text-xs mb-1">スコア</div>
                        <div className="text-xl font-display font-bold text-green-500">A</div>
                    </div>
                </div>

                <Button variant="destructive" className="w-full py-6 text-lg font-bold shadow-[0_0_20px_rgba(255,0,0,0.3)]" onClick={onPunish}>
                    処罰メニューを開く
                </Button>
            </CardContent>
        </Card>
    );
};
